// frontend/src/lib/dock-utils.ts

export type DisplayStatus = 'Available' | 'Occupied' | 'Maintenance' | 'Out of Service';

const normalize = (s?: string | null) => (s ?? '').trim().toLowerCase();

export const CHECKED_OUT_STATUSES = [
  'checked_out',
  'checked out',
  'completed',
  'done',
  'departed',
  'closed',
  'finished'
];

/**
 * Convert any raw status string into a strict UI display status.
 * Normalization-heavy so all pages render consistently even if backend strings vary.
 */
export const toDisplayStatus = (raw?: string | null): DisplayStatus => {
  const s = normalize(raw);

  // Default: empty/unknown -> Available (optimistic)
  if (!s) return 'Available';

  // Maintenance (highest priority label)
  if (['maintenance', 'maint', 'maintain'].includes(s)) return 'Maintenance';

  // Out of Service variants
  if (
    [
      'out_of_service',
      'out of service',
      'oos',
      'disabled',
      'offline',
      'down',
      'closed',
      'inactive',
    ].includes(s)
  ) {
    return 'Out of Service';
  }

  // Occupied variants (including appointment-like statuses)
  if (
    [
      'occupied',
      'busy',
      'loading',
      'unloading',
      'in_progress',
      'in progress',
      'checked_in',
      'checked in',
      'arrived',
      'active',
      'working',
      'processing',
      'reserved',
    ].includes(s)
  ) {
    return 'Occupied';
  }

  // Available variants
  if (['available', 'open', 'idle', 'ready', 'free', 'vacant'].includes(s)) {
    return 'Available';
  }

  // Unknown -> Available (change to 'Occupied' if you prefer conservative)
  return 'Available';
};

export interface MinimalDock {
  status?: string | null; // stored/manual intent (persisted)
  effectiveStatus?: string | null; // backend canonical decision when present
  now?: {
    status?: string | null;
    checkInTime?: string | null;
    checkOutTime?: string | null;
  } | null;
}

/**
 * Single canonical source of truth for dock status across the frontend:
 *
 * Priority (explicit):
 * 1) Manual stored status if it is Maintenance / Out of Service (hard lock).
 * 2) Active "now" appointment (exists and not checked out) -> Occupied.
 * 3) Backend effectiveStatus if present.
 * 4) Fallback to stored status (normalized).
 */
export function getDockDisplayStatus(dock: MinimalDock): DisplayStatus {
  // 1) Manual lock: Maintenance / OOS always win if manually set
  const stored = toDisplayStatus(dock.status);
  if (stored === 'Maintenance' || stored === 'Out of Service') return stored;

  // 2) Active load priority - TRUTH ON THE GROUND
  // If we have an active "now" appointment that isn't checked out, IT IS OCCUPIED.
  // This overrides backend "Available" signals which might be stale or pure-db based.
  const hasNow = !!dock.now;

  // Determine if effectively checked out (departed)
  // Check both explicit timestamp OR status flag
  const isCheckedOutTimePresent = !!dock.now?.checkOutTime;
  const nowStatus = normalize(dock.now?.status);
  const isCompletedStatus = CHECKED_OUT_STATUSES.includes(nowStatus);

  const checkedOut = isCheckedOutTimePresent || isCompletedStatus;

  if (hasNow && !checkedOut) return 'Occupied';

  // 3) Backend effective decision
  if (dock.effectiveStatus) {
    return toDisplayStatus(dock.effectiveStatus);
  }

  // 4) Fallback to stored
  return stored;
}

/**
 * Normalize to one of the four select-safe values.
 * Use this for <select value={...}> to avoid invalid/uncontrolled values.
 */
export const toManualSelectValue = (raw?: string | null): DisplayStatus => {
  // Manual intent should be one of the same 4 labels.
  // If backend stored variants like "oos" or "out_of_service", this cleans it.
  return toDisplayStatus(raw);
};

/**
 * Convenience helpers
 */
export const isDockOccupied = (dock: MinimalDock) => getDockDisplayStatus(dock) === 'Occupied';
export const isDockAvailable = (dock: MinimalDock) => getDockDisplayStatus(dock) === 'Available';
export const isDockMaintenance = (dock: MinimalDock) => getDockDisplayStatus(dock) === 'Maintenance';
export const isDockOutOfService = (dock: MinimalDock) => getDockDisplayStatus(dock) === 'Out of Service';

/**
 * Debug Helper (Non-Visual)
 * Returns the exact decision path used by getDockDisplayStatus.
 * Useful for verifying why a dock is showing as Available/Occupied.
 */
export function getDockStatusDebug(dock: MinimalDock): {
  stored: DisplayStatus;
  effective?: DisplayStatus;
  hasNow: boolean;
  checkedOut: boolean;
  checkedOutReason: 'checkoutTime' | 'completedStatus' | 'none';
  nowStatus: string;
  nowCheckOutTime: string | null;
} {
  const stored = toDisplayStatus(dock.status);
  const hasNow = !!dock.now;
  const isCheckedOutTimePresent = !!dock.now?.checkOutTime;
  const nowStatus = normalize(dock.now?.status);
  const isCompletedStatus = CHECKED_OUT_STATUSES.includes(nowStatus);

  const checkedOut = isCheckedOutTimePresent || isCompletedStatus;

  let checkedOutReason: 'checkoutTime' | 'completedStatus' | 'none' = 'none';
  if (isCheckedOutTimePresent) checkedOutReason = 'checkoutTime';
  else if (isCompletedStatus) checkedOutReason = 'completedStatus';

  return {
    stored,
    effective: dock.effectiveStatus ? toDisplayStatus(dock.effectiveStatus) : undefined,
    hasNow,
    checkedOut,
    checkedOutReason,
    nowStatus,
    nowCheckOutTime: dock.now?.checkOutTime || null,
  };
}
